import { adminDelete, adminGet, adminPatch, adminPost } from "@lib/adminApi";

import type { User } from "./types";
import type { UserStatus } from "./types";

// Admin user endpoints on omg-api (OMG-2092, replacing the legacy GraphQL).

// Customer notices (OMG-2558). Mirrors NOTICE_TYPES in omg-api
// models/customerNote.ts; the API rejects anything else.
export const NOTICE_TYPES = [
  "arbitration_opt_out",
  "tos_change_opt_out",
  "privacy_request",
  "notice_of_dispute",
  "other",
] as const;

export type NoticeType = (typeof NOTICE_TYPES)[number];

/** A notice on file, as the user header and the users list show it. */
export interface AdminUserNotice {
  noteId: number;
  type: NoticeType;
  /** Calendar date received, `YYYY-MM-DD` (no time zone). */
  receivedOn: string;
  recordedAt: string;
}

/** One append-only admin note. The author comes from the server, never the form. */
export interface AdminUserNote {
  id: number;
  body: string;
  url: string | null;
  notice: { type: NoticeType; receivedOn: string } | null;
  authorSub: string;
  authorEmail: string | null;
  createdAt: string;
}

// `notices` is optional in both shapes on purpose: an omg-api that predates
// OMG-2558 omits it, and "unknown" must not render as "no notice on file".
export interface AdminUserListItem extends User {
  notices?: AdminUserNotice[];
}

export interface AdminUserListResponse {
  users: AdminUserListItem[];
  total: number;
  limit: number;
  offset: number;
}

// Video preferences the admin can read (and, for two of them, write) per user.
export interface AdminUserVideoPreferences {
  explicitContent: boolean;
  explicitEligible: boolean;
  explicitOverlayShown: boolean;
}

// Detail view: the list row plus entitlements (names) and video preferences,
// so the User page loads in one request instead of a user -> prefs waterfall.
export interface AdminUserDetail {
  id: string;
  email: string;
  status: UserStatus;
  createdAt: string;
  entitlements: string[];
  preferences: AdminUserVideoPreferences;
  notices?: AdminUserNotice[];
}

// Request bodies are `type` aliases (not `interface`) so they satisfy the
// `Record<string, unknown>` body parameter on adminPost/adminPatch.
export type CreateAdminUserBody = {
  email: string;
  password: string;
  entitlements?: string[];
};

export type UpdateAdminUserBody = {
  email?: string;
  entitlements?: string[];
  preferences?: Partial<
    Pick<AdminUserVideoPreferences, "explicitContent" | "explicitEligible">
  >;
};

export type CreateAdminUserNoteBody = {
  body: string;
  url?: string;
  notice?: { type: NoticeType; receivedOn: string };
};

/** Users-list notice filter: any notice on file, or one type. */
export type NoticeFilter = "any" | NoticeType;

export interface SearchAdminUsersParams {
  email?: string;
  sortBy: "email" | "createdAt";
  sortOrder: "asc" | "desc";
  createdAtFrom?: string;
  createdAtTo?: string;
  noticeType?: NoticeFilter;
  limit: number;
  offset: number;
}

export const searchAdminUsers = (
  params: SearchAdminUsersParams,
): Promise<AdminUserListResponse> => {
  const qs = new URLSearchParams();
  if (params.email) qs.set("email", params.email);
  if (params.noticeType) qs.set("noticeType", params.noticeType);
  qs.set("sortBy", params.sortBy);
  qs.set("sortOrder", params.sortOrder);
  if (params.createdAtFrom) qs.set("createdAtFrom", params.createdAtFrom);
  if (params.createdAtTo) qs.set("createdAtTo", params.createdAtTo);
  qs.set("limit", String(params.limit));
  qs.set("offset", String(params.offset));
  return adminGet<AdminUserListResponse>(`/admin/users?${qs.toString()}`);
};

export const getAdminUser = (id: string): Promise<AdminUserDetail> =>
  adminGet<AdminUserDetail>(`/admin/users/${encodeURIComponent(id)}`);

export const createAdminUser = (
  body: CreateAdminUserBody,
): Promise<AdminUserDetail> => adminPost<AdminUserDetail>("/admin/users", body);

export const updateAdminUser = (
  id: string,
  body: UpdateAdminUserBody,
): Promise<AdminUserDetail> =>
  adminPatch<AdminUserDetail>(`/admin/users/${encodeURIComponent(id)}`, body);

export const deleteAdminUser = (id: string): Promise<void> =>
  adminDelete(`/admin/users/${encodeURIComponent(id)}`);

export const deactivateAdminUser = (id: string): Promise<AdminUserDetail> =>
  adminPost<AdminUserDetail>(
    `/admin/users/${encodeURIComponent(id)}/deactivate`,
  );

export const reactivateAdminUser = (id: string): Promise<AdminUserDetail> =>
  adminPost<AdminUserDetail>(
    `/admin/users/${encodeURIComponent(id)}/reactivate`,
  );

// Admin notes (OMG-2558). Append-only: there is no update or delete call
// because omg-api has no such route and the table rejects both.
export const listAdminUserNotes = (
  id: string,
): Promise<{ notes: AdminUserNote[] }> =>
  adminGet<{ notes: AdminUserNote[] }>(
    `/admin/users/${encodeURIComponent(id)}/notes`,
  );

export const createAdminUserNote = (
  id: string,
  body: CreateAdminUserNoteBody,
): Promise<AdminUserNote> =>
  adminPost<AdminUserNote>(
    `/admin/users/${encodeURIComponent(id)}/notes`,
    body,
  );

export const setAdminUserPassword = (
  id: string,
  password: string,
): Promise<void> =>
  adminPost<void>(`/admin/users/${encodeURIComponent(id)}/set-password`, {
    password,
  });

// Generate a strong random password that satisfies the Cognito pool policy
// (upper + lower + digit + symbol, no leading/trailing whitespace). Used for
// the admin "Generate Password" action and to seed the required password on
// create (the created user resets via forgot-password, as under the legacy
// flow). Excludes visually ambiguous chars (O/0, I/l/1).
const PASSWORD_UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const PASSWORD_LOWER = "abcdefghijkmnopqrstuvwxyz";
const PASSWORD_DIGITS = "23456789";
const PASSWORD_SYMBOLS = "!@#$%^&*-_";

export const generateAdminPassword = (length = 20): string => {
  const groups = [
    PASSWORD_UPPER,
    PASSWORD_LOWER,
    PASSWORD_DIGITS,
    PASSWORD_SYMBOLS,
  ];
  const all = groups.join("");
  // Never shorter than the number of required classes, or the per-class chars
  // below would read past the random array and emit "undefined".
  const size = Math.max(length, groups.length);
  const rand = new Uint32Array(size);
  crypto.getRandomValues(rand);
  // One char from each required class up front, then fill from the full set.
  const chars = groups.map((group, i) => group[rand[i] % group.length]);
  for (let i = groups.length; i < size; i++) {
    chars.push(all[rand[i] % all.length]);
  }
  // Fisher-Yates shuffle so the guaranteed chars aren't always positions 0-3.
  const swaps = new Uint32Array(chars.length);
  crypto.getRandomValues(swaps);
  for (let i = chars.length - 1; i > 0; i--) {
    const j = swaps[i] % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
};
